import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ResolvedDocument } from "../dtos";
import { useStepMutations } from "../hooks/use-step-mutations.hook";
import { primaryButtonSx, secondaryButtonSx } from "./field-shell";
import {NoteAttachmentComposer, useNoteAttachmentDraft} from "./note-attachments";

const DOC_TYPE_SEPARATOR = " — ";

/** The grouped catalogue: structured docCategories when served, else re-split the flat fileTypes
 * on the composed separator (legacy definitions). */
const catalogueOf = (
  document: ResolvedDocument | null | undefined,
  fileTypes?: string[]
): { name: string; types: string[] }[] => {
  if (document?.docCategories?.length) {
    return document.docCategories.map((category) => ({
      name: category.name,
      types: category.types.map((type) => type.name),
    }));
  }
  const groups = new Map<string, string[]>();
  for (const composed of fileTypes ?? []) {
    const [category, type] = composed.includes(DOC_TYPE_SEPARATOR)
      ? composed.split(DOC_TYPE_SEPARATOR)
      : ["", composed];
    groups.set(category, [...(groups.get(category) ?? []), type]);
  }
  return [...groups.entries()].map(([name, types]) => ({ name, types }));
};

/**
 * §Doc-request authoring (WM parity): pick which catalogue documents this placement needs and how —
 * "Request from client" surfaces them in the customer's app and notifies them; "Upload on behalf"
 * keeps the slots internal. Saving with nothing selected clears the request.
 */
const DocRequestDialog = ({
  open,
  onClose,
  docKey,
  caseUid,
  stepUid,
  document,
  fileTypes,
  mutations,
}: {
  open: boolean;
  onClose: () => void;
  docKey: string;
  caseUid?: string | null;
  stepUid?: string;
  document?: ResolvedDocument | null;
  fileTypes?: string[];
  mutations: ReturnType<typeof useStepMutations>;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const [mode, setMode] = useState<"FROM_CLIENT" | "ON_BEHALF">("FROM_CLIENT");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const attachments = useNoteAttachmentDraft({
    caseUid,
    context: caseUid && stepUid ? {kind: "document-request", stepUid, fieldKey: docKey} : null,
  });
  const discardAttachments = attachments.discard;

  useEffect(() => {
    if (selected.size === 0) discardAttachments();
  }, [discardAttachments, selected.size]);

  // seed from the live request each time the dialog opens (edit = same surface)
  useEffect(() => {
    if (!open) return;
    setMode(document?.request?.mode ?? "FROM_CLIENT");
    setSelected(
      new Set(
        (document?.request?.items ?? []).map((item) =>
          // JSON tuple key — category names carry spaces, so no string separator is safe
          JSON.stringify([item.category ?? "", item.type])
        )
      )
    );
  }, [open, document?.request]);

  const catalogue = catalogueOf(document, fileTypes);
  const keyOf = (category: string, type: string) => JSON.stringify([category, type]);
  const saving = mutations.docRequest.isLoading;
  const toggle = (category: string, type: string) => {
    if (saving) return;
    const key = keyOf(category, type);
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = () => {
    mutations.docRequest.mutateAsync({
        docKey,
        mode,
        items: [...selected].map((key) => {
          const [category, type] = JSON.parse(key) as [string, string];
          return { category, type };
        }),
        attachmentUids: attachments.attachmentUids,
      }).then(() => {
        attachments.markSubmitted();
        onClose();
      }, () => undefined);
  };
  const cancel = () => {
    if (saving) return;
    attachments.discard();
    onClose();
  };

  // §MobileQA: was a raw MUI <Dialog>, so it stayed a centred desktop dialog at 390px while every
  // other modal in the tab became a bottom sheet. ResponsiveModal picks MobileModal (react-modal-
  // sheet) below 743px and CommonModal above, so this now matches the design on both.
  return (
    <ResponsiveModal open={open} onClose={cancel} title="Request extra documents">
      <Box
        sx={{
          width: { mobile: "100%", desktop: 560 },
          maxWidth: "100%",
          // §MobileQA (2026-08-16): the sheet scrolls itself (MobileModal's Sheet.Scroller) — the
          // old 70vh inner scroller out-measured the visible sheet, so the checklist's bottom
          // items could never be reached on a phone.
          maxHeight: { mobile: "none", desktop: 440 },
          overflowY: { mobile: "visible", desktop: "auto" },
          // design: the sheet body is inset 16. MobileModal already pads its children by
          // spacing.md, so padding here would double it to 36; CommonModal pads nothing, so the
          // inset still has to be supplied on the desktop branch.
          px: { mobile: 0, tablet: 2.5 },
          py: { mobile: 0, tablet: 2 },
        }}
      >
        {/* mode: who does the uploading */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          {(
            [
              ["FROM_CLIENT", "Request from client"],
              ["ON_BEHALF", "Upload on behalf"],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={value}
              size="small"
              variant={mode === value ? "contained" : "outlined"}
              disabled={saving}
              onClick={() => setMode(value)}
              sx={mode === value ? primaryButtonSx(theme) : secondaryButtonSx(theme)}
            >
              {label}
            </Button>
          ))}
        </Stack>
        {mode === "FROM_CLIENT" && (
          <Typography
            variant="caption"
            color={colors.semantic.text.secondary}
            sx={{ display: "block", mb: 1 }}
          >
            The client sees these in their app and gets notified.
          </Typography>
        )}
        {catalogue.map((category) => (
          <Box key={category.name} sx={{ mb: 1 }}>
            {category.name && (
              <Typography
                sx={{
                  fontSize: 11,
                  lineHeight: "16px",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: colors.semantic.text.secondary,
                  mt: 1,
                }}
              >
                {category.name}
              </Typography>
            )}
            {category.types.map((type) => (
              <FormControlLabel
                key={type}
                sx={{ display: "flex", ml: 0, "& .MuiTypography-root": { fontSize: 14 } }}
                control={
                  <Checkbox
                    size="small"
                    disabled={saving}
                    checked={selected.has(keyOf(category.name, type))}
                    onChange={() => toggle(category.name, type)}
                  />
                }
                label={type}
              />
            ))}
          </Box>
        ))}
        {selected.size > 0 && <NoteAttachmentComposer draft={attachments} disabled={saving} />}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{
          // same doubling as the body above — the footer band keeps its own vertical rhythm
          px: { mobile: 0, tablet: 2.5 },
          py: 2,
          borderTop: `1px solid ${colors.semantic.border.secondary}`,
        }}
      >
        <Button disabled={saving} onClick={cancel} sx={secondaryButtonSx(theme)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving || attachments.pending || attachments.hasErrors}
          startIcon={saving ? <CircularProgress size={13} /> : undefined}
          onClick={save}
          sx={primaryButtonSx(theme)}
        >
          {selected.size === 0 && document?.request ? "Clear request" : "Save request"}
        </Button>
      </Stack>
    </ResponsiveModal>
  );
};

export default DocRequestDialog;
